/**
 * La trousse en vitrine : en grand, dans l'aura HORA de sa rareté (même moteur que l'aura des SKILLCARDS,
 * `AvatarAura` : la puissance grandit avec le rang, la teinte suit le palier de rareté), qui flotte et
 * dont le zip s'entrouvre comme sur le tableau de bord. `celebrate` = l'entrée qui claque (gagnée à la
 * roue, reçue en cadeau, obtenue par échange). Purement décoratif : le nom est écrit à côté.
 */
import type { CSSProperties } from 'react';
import { AvatarAura } from '@/features/skillcards/avatar-aura/avatar-aura';
import {
  trousseAuraPower,
  trousseImage,
  trousseRarity,
  trousseRarityColor,
  TROUSSE_RARITIES,
} from '@/lib/trousses';
import './trousse.css';
import './dashboard-trousse.css';
import './trousse-showcase.css';

/** Étincelles autour de la trousse : position (px) et décalage d'animation. */
const SPARKS = [
  { x: -139, y: -58, d: 0 },
  { x: 133, y: -81, d: 0.5 },
  { x: -102, y: 102, d: 1.1 },
  { x: 151, y: 64, d: 1.6 },
  { x: 12, y: -151, d: 0.8 },
  { x: -14, y: 145, d: 1.9 },
  { x: -171, y: 12, d: 1.3 },
  { x: 174, y: -9, d: 0.3 },
];

export function TrousseShowcase({
  trousse,
  celebrate = false,
  className,
}: {
  trousse: { rank: number; copies: number; image_path: string };
  celebrate?: boolean;
  className?: string;
}) {
  const rarity = trousseRarity(trousse.copies);
  const tier = TROUSSE_RARITIES.indexOf(rarity);
  const color = trousseRarityColor(trousse.copies);
  // plus c'est rare, plus il y a d'étincelles (au moins 3 quand on la célèbre)
  const sparks = SPARKS.slice(0, Math.max(celebrate ? 3 : 0, tier + (tier >= 2 ? 2 : 0)));
  const style = {
    '--tshow-color': color,
    // commune : à peine un voile ; puis de plus en plus marqués avec la rareté
    '--tshow-rays': String(tier === 0 ? 0.06 : 0.16 + tier * 0.12),
  } as CSSProperties;

  return (
    <div
      className={`jr-tshow${className ? ` ${className}` : ''}`}
      data-celebrate={celebrate ? '1' : '0'}
      data-rarity={rarity}
      data-testid="trousse-showcase"
      style={style}
      aria-hidden
    >
      <span className="jr-tshow-rays" />
      <AvatarAura power={trousseAuraPower(trousse.rank)} color={color} className="jr-tshow-aura">
        <span className="jr-tshow-piece">
          <span className="jr-dtr">
            <span className="jr-dtr-shadow" />
            <span className="jr-dtr-body">
              <span className="jr-dtr-turn">
                <i
                  className="jr-dtr-cover"
                  style={{ backgroundImage: `url(${trousseImage(trousse.image_path)})` }}
                />
                <span className="jr-tr-zip">
                  <i className="jr-tr-rail" />
                  <i className="jr-tr-gap" />
                  <i className="jr-tr-pull" />
                </span>
              </span>
            </span>
          </span>
        </span>
      </AvatarAura>
      {sparks.map((p, i) => (
        <span
          key={i}
          className="jr-tshow-spark"
          style={{ '--x': `${p.x}px`, '--y': `${p.y}px`, '--d': `${p.d}s` } as CSSProperties}
        />
      ))}
      <span className="jr-tshow-burst" />
    </div>
  );
}
